import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import NumberFormat from 'react-number-format';
import {config, Spring} from 'react-spring/renderprops';
import KeyCode from '../../constants/keys';
import {usePrevious} from '../../hooks/usePrevious';
import PanelContainer from './../PanelContainer';
import ListActionControls from './components/ListActionControls';
import ListSelectControlItem from './components/ListSelectControlItem';
import ListSortControls from './components/ListSortControls';

import './styles.css';

const ListSearch = ({
  sortBy,
  sortByLabel,
  sortControls,
  filter,
  filterControls,
  recordsCount,
  recordsName,
  recordsPrefixName,
  statsCount,
  statsName,
  statsPrefixName,
  actionSelectControls,
  actionButtonControls,
  search,
  searchPlaceholder,
  searchQueryMinLength,
  searchQueryMaxLength,
  onChangeFilterHandler,
  onChangeSortByHandler,
  onSendSearchQueryHandler,
  renderTabs,
  children,
  renderHeader,
  noResultsPlaceholder = 'No results',
  hideSearchInput = false,
  isFetching = false,
  isActionSelectControlsDisabled,
}) => {
  const [query, changeQuery] = useState(search || '');
  const input = useRef();
  const recordCountRef = usePrevious(Number(recordsCount) || 0);
  const statsCountRef = usePrevious(Number(statsCount) || 0);

  useEffect(() => {
    if (search) {
      changeQuery(search);
      input.current.blur();
    } else if (search === '' || (!search && !hideSearchInput)) {
      changeQuery('');
      input.current.blur();
    }
  }, [search]);

  const searchQueryHandler = e => {
    const { value } = e.target;
    if (value.length <= searchQueryMaxLength) {
      changeQuery(value);
    }
  };

  const handleKeyPress = e => {
    if (e.key === KeyCode.ENTER && query.length >= searchQueryMinLength) {
      onSendSearchQueryHandler(query);
    }
  };

  const handleClickButton = e => {
    if (query.length >= searchQueryMinLength) {
      onSendSearchQueryHandler(query);
    }
  };

  const showAdditionalControls = actionSelectControls || actionButtonControls;

  return (
    <PanelContainer>
      <div className="list-search">
        {renderHeader && (
          <div className="list-search__header">{renderHeader}</div>
        )}
        {renderTabs && <div className="list-search__tabs">{renderTabs}</div>}

        {!hideSearchInput && (
          <div className="list-search__main">
            <input
              ref={input}
              className="list-search__input"
              name="Search"
              type="text"
              value={query}
              onChange={searchQueryHandler}
              placeholder={searchPlaceholder}
              onKeyPress={handleKeyPress}
            />
            <button className="list-search__btn" onClick={handleClickButton} />
          </div>
        )}

        <div
          className={
            recordsCount > 0
              ? 'list-search__footer list-search__footer--with-counter'
              : 'list-search__footer list-search__footer--without-counter'
          }
        >
          {filterControls && (
            <div className="list-search__filter-controls">
              <ListSortControls
                sortBy={filter}
                sortByLabel={'Filter By:'}
                onSortByChangeHandler={onChangeFilterHandler}
                sortControls={filterControls}
              />
            </div>
          )}

          <div className="list-search__counters">
            {Number(recordsCount) > 0 ? (
              <div className="list-search__counter">
                <span className="list-search__link">
                  <Spring
                    from={{ value: recordCountRef }}
                    to={{ value: Number(recordsCount) }}
                    config={config.wobbly}
                  >
                    {props => (
                      <NumberFormat
                        thousandSeparator
                        value={props.value.toFixed(0)}
                        displayType={'text'}
                      />
                    )}
                  </Spring>{' '}
                  <span>{recordsPrefixName}</span>
                </span>{' '}
                <span>{recordsName}</span>
              </div>
            ) : (
              recordsName &&
              (Number(statsCount) === 0 || !statsCount) &&
              !isFetching && (
                <div className="list-search__no-results">
                  {noResultsPlaceholder}
                </div>
              )
            )}

            {statsCount > 0 && (
              <div className="list-search__counter">
                <span className="list-search__link">
                  <Spring
                    from={{ value: statsCountRef }}
                    to={{ value: Number(statsCount) }}
                    config={config.wobbly}
                  >
                    {props => (
                      <NumberFormat
                        thousandSeparator
                        value={props.value.toFixed(0)}
                        displayType={'text'}
                      />
                    )}
                  </Spring>{' '}
                  <span> {statsPrefixName} </span>
                </span>
                {''} {statsName}
              </div>
            )}
          </div>

          <div className="list-search__sorting-controls">
            {sortControls && (
              <ListSortControls
                sortBy={sortBy}
                sortByLabel={sortByLabel}
                onSortByChangeHandler={onChangeSortByHandler}
                sortControls={sortControls}
              />
            )}
          </div>
        </div>
        {showAdditionalControls && (
          <div className="list-search__additional-controls">
            {actionSelectControls && (
              <div className="list-search__action-buttons">
                <div className="list-select-controls">
                  {actionSelectControls.map(
                    (
                      {
                        caption,
                        captionActive = 'Clear All',
                        iconActive = null,
                        icon,
                        controlFunc,
                      },
                      index
                    ) => (
                      <div className="list-select-controls__button" key={index}>
                        <ListSelectControlItem
                          disabled={isActionSelectControlsDisabled}
                          caption={caption}
                          captionActive={captionActive}
                          iconActive={iconActive}
                          icon={icon}
                          controlFunc={controlFunc}
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            {actionButtonControls && (
              <div className="list-search__action-buttons">
                <ListActionControls
                  actionButtonControls={actionButtonControls}
                />
              </div>
            )}
          </div>
        )}
        {children && <div className="list-search__external">{children}</div>}
      </div>
    </PanelContainer>
  );
};

ListSearch.defaultProps = {
  searchPlaceholder: 'Search',
  recordsCount: 0,
  searchQueryMinLength: 0,
  searchQueryMaxLength: 100,
};

ListSearch.propTypes = {
  hideSearchInput: PropTypes.bool,
  searchQueryMinLength: PropTypes.number,
  searchQueryMaxLength: PropTypes.number,
  search: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  searchPlaceholder: PropTypes.string,
  recordsName: PropTypes.string,
  recordsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sortBy: PropTypes.any,
  sortByLabel: PropTypes.string,
  onChangeSortByHandler: PropTypes.func,
  onSendSearchQueryHandler: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  isActionSelectControlsDisabled: PropTypes.bool,
};

export default ListSearch;
